import React, { useContext, useMemo } from 'react';
import PropTypes from 'prop-types';
import WalletProp, { WALLET_TYPE } from '../../proptype/walletProp';
import translate from '../../../../commonComponents/translate';
import { newFormatPrice } from '../../../../services/utilityFunctions';
import WalletIcon from './icons/WalletIcon';
import CoinsIcon from './icons/CoinsIcon';
import WithdrawIcon from './icons/WithdrawIcon';
import ExchangeIcon from './icons/ExchangeIcon';
import ConfigsContext from '../../../../contextProviders/ConfigsContext';
import LoggedUserContext from '../../../../contextProviders/LoggedUserContext';
import {
  formatDecimalPrices,
  formatNumber,
  isSmallerThanTwoDecimal,
} from '../../../../services/numberUtilis';

function CommissionsWalletCardListItem(props) {
  const {
    t,
    wallet,
    isActive,
    onSelect,
    onSeeTransactions,
    onWithdrawal,
    onExchange,
  } = props;
  const { organization } = useContext(ConfigsContext);
  const { loggedUser } = useContext(LoggedUserContext);
  const { isWithdrawalsEnabled } = loggedUser?.walletSettings || {};
  const { enabled: isCharityCoinsEnabled } =
    organization?.services?.charity || {};

  const { walletConfiguration } =
    organization?.services?.wallet_advanced?.config || {};
  const {
    type,
    currencyInfo,
    balance,
    userPriceBalance,
    available: walletAvailable,
    userPriceAvailable,
    coinsAvailable,
    charityCoinsAvailable,
    totalPayments,
    userPriceTotalPayments,
    totalPayouts,
    userPriceTotalPayouts,
    coinsPayments = 0,
    allowedConversions = [],
  } = wallet;

  const showExchangeButton = (allowedConversions?.length || 0) > 0;

  const isCoinsWallet =
    type === WALLET_TYPE.COINS || type === WALLET_TYPE.CHARITY;
  const showAvailableBalance =
    type === WALLET_TYPE.FIAT || type === WALLET_TYPE.CRYPTO;
  const showExtraInformation =
    isCoinsWallet && userPriceAvailable && userPriceBalance;

  const showTransactions = [
    WALLET_TYPE.FIAT,
    WALLET_TYPE.CRYPTO,
    WALLET_TYPE.NEO_DEAL,
    WALLET_TYPE.NEO_POOL,
  ].includes(type);
  const showWithdrawButton = [WALLET_TYPE.FIAT, WALLET_TYPE.CRYPTO].includes(
    type,
  );
  const showWithdraws = [WALLET_TYPE.FIAT, WALLET_TYPE.CRYPTO].includes(type);

  const cryptoBalance = balance ? Number(balance) : 0;
  const fiatBalance = userPriceBalance?.amount || 0;
  const walletBalance =
    type === WALLET_TYPE.FIAT ? fiatBalance : cryptoBalance;
  const isWithdrawEnabled = walletBalance > 0 && isWithdrawalsEnabled;

  const icon = useMemo(() => {
    switch (type) {
      case WALLET_TYPE.CRYPTO:
        return <img src={currencyInfo.icon} alt="crypto logo" />;
      case WALLET_TYPE.FIAT:
        return walletConfiguration?.[type]?.icon?.url ? (
          <img src={walletConfiguration?.[type]?.icon?.url} alt="fiat logo" />
        ) : (
          <WalletIcon />
        );
      case WALLET_TYPE.COINS:
        return walletConfiguration?.[type]?.icon?.url ? (
          <img src={walletConfiguration?.[type]?.icon?.url} alt="coins logo" />
        ) : (
          <CoinsIcon />
        );
      case WALLET_TYPE.CHARITY:
        return walletConfiguration?.[type]?.icon?.url ? (
          <img src={walletConfiguration?.[type]?.icon?.url} alt="coins logo" />
        ) : (
          <CoinsIcon />
        );
      case WALLET_TYPE.NEO_DEAL:
        return walletConfiguration?.[type]?.icon?.url ? (
          <img src={walletConfiguration?.[type]?.icon?.url} alt="fiat logo" />
        ) : (
          <WalletIcon />
        );
      case WALLET_TYPE.NEO_POOL:
        return walletConfiguration?.[type]?.icon?.url ? (
          <img src={walletConfiguration?.[type]?.icon?.url} alt="fiat logo" />
        ) : (
          <WalletIcon />
        );
      default:
        return <WalletIcon />;
    }
  }, [type, currencyInfo, walletConfiguration]);

  const iconClassWrapper = useMemo(() => {
    switch (type) {
      case WALLET_TYPE.COINS:
        return walletConfiguration?.[type]?.icon?.url
          ? 'custom-image'
          : 'commissions-wallet-picker-card-header-icon commissions-wallet-picker-card-header-icon-coins';
      case WALLET_TYPE.FIAT:
        return walletConfiguration?.[type]?.icon?.url
          ? 'custom-image'
          : 'commissions-wallet-picker-card-header-icon';
      case WALLET_TYPE.CHARITY:
        return walletConfiguration?.[type]?.icon?.url
          ? 'custom-image'
          : 'commissions-wallet-picker-card-header-icon commissions-wallet-picker-card-header-icon-coins';
      case WALLET_TYPE.NEO_DEAL:
        return walletConfiguration?.[type]?.icon?.url
          ? 'custom-image'
          : 'commissions-wallet-picker-card-header-icon';
      case WALLET_TYPE.NEO_POOL:
        return walletConfiguration?.[type]?.icon?.url
          ? 'custom-image'
          : 'commissions-wallet-picker-card-header-icon';
      default:
        return 'commissions-wallet-picker-card-header-icon';
    }
  }, [type]);

  const walletName = useMemo(() => {
    switch (type) {
      case WALLET_TYPE.CRYPTO:
        return currencyInfo?.name;
      default:
        return (
          walletConfiguration?.[type]?.name ||
          t(`commissions.walletType.${type}`)
        );
    }
  }, [type, currencyInfo?.name]);

  const cryptoCurrency = useMemo(
    () => currencyInfo?.symbol_native || currencyInfo?.symbol,
    [currencyInfo],
  );

  const formatValue = (value, code) =>
    isSmallerThanTwoDecimal(Math.floor(value) || 0)
      ? formatDecimalPrices(Math.floor(value) || 0, true, code)
      : newFormatPrice(Math.floor(value) || 0, true, code, true);

  const totalBalanceTxt = useMemo(() => {
    switch (type) {
      case WALLET_TYPE.COINS:
      case WALLET_TYPE.CHARITY:
        return userPriceBalance
          ? t('commissions.totalBalance')
          : t('rewards_coins');
      default:
        return t('commissions.totalBalance');
    }
  }, [type, userPriceBalance, t]);

  const totalBalance = useMemo(() => {
    switch (type) {
      case WALLET_TYPE.FIAT:
        return formatValue(userPriceBalance?.amount, userPriceBalance?.code);
      case WALLET_TYPE.CRYPTO:
        return `${balance || 0}${cryptoCurrency}`;
      case WALLET_TYPE.COINS:
        return userPriceBalance
          ? formatValue(userPriceBalance?.amount, userPriceBalance?.code)
          : balance || 0;
      case WALLET_TYPE.CHARITY:
        return userPriceBalance
          ? formatValue(userPriceBalance?.amount, userPriceBalance?.code)
          : balance || 0;
      case WALLET_TYPE.NEO_DEAL:
      case WALLET_TYPE.NEO_POOL:
        return formatValue(userPriceBalance?.amount, userPriceBalance?.code);
      default:
        return balance || 0;
    }
  }, [type, balance, cryptoCurrency, userPriceBalance]);

  const available = useMemo(() => {
    switch (type) {
      case WALLET_TYPE.FIAT:
        return formatValue(
          userPriceAvailable?.amount,
          userPriceAvailable?.code,
        );
      case WALLET_TYPE.CRYPTO:
        return `${walletAvailable || 0}${cryptoCurrency}`;
      case WALLET_TYPE.COINS:
        return userPriceAvailable
          ? formatValue(userPriceAvailable?.amount, userPriceAvailable?.code)
          : walletAvailable || 0;
      case WALLET_TYPE.CHARITY:
        return userPriceAvailable
          ? formatValue(userPriceAvailable?.amount, userPriceAvailable?.code)
          : walletAvailable || 0;
      case WALLET_TYPE.NEO_DEAL:
      case WALLET_TYPE.NEO_POOL:
        return formatValue(
          userPriceAvailable?.amount,
          userPriceAvailable?.code,
        );
      default:
        return walletAvailable || 0;
    }
  }, [type, walletAvailable, cryptoCurrency, userPriceAvailable]);

  // Commission info
  const totalOfPaymentsTxt = useMemo(() => {
    switch (type) {
      case WALLET_TYPE.FIAT:
      case WALLET_TYPE.CRYPTO:
        return t('commissions.total.totalCommissions');
      case WALLET_TYPE.COINS:
      case WALLET_TYPE.CHARITY:
        return t('total.of.coins');
      case WALLET_TYPE.NEO_DEAL:
      case WALLET_TYPE.NEO_POOL:
        return t('commissions.totalFund');
      default:
        return null;
    }
  }, [t, type]);

  const totalOfPayments = useMemo(() => {
    switch (type) {
      case WALLET_TYPE.FIAT:
        return newFormatPrice(
          parseFloat(
            Math.floor(parseFloat(userPriceTotalPayments?.price)),
          ) || 0,
          true,
          userPriceTotalPayments?.code || '',
        );
      case WALLET_TYPE.CRYPTO:
        return `${totalPayments || 0}${currencyInfo?.symbol || ''}`;
      case WALLET_TYPE.COINS:
      case WALLET_TYPE.CHARITY:
        return formatNumber(coinsPayments);
      case WALLET_TYPE.NEO_DEAL:
      case WALLET_TYPE.NEO_POOL:
        return newFormatPrice(
          parseFloat(
            Math.floor(parseFloat(userPriceTotalPayments?.price)),
          ) || 0,
          true,
          userPriceTotalPayments?.code || '',
        );
      default:
        return null;
    }
  }, [type, userPriceTotalPayments, totalPayments, currencyInfo, coinsPayments]);

  const totalOfPayouts = useMemo(() => {
    switch (type) {
      case WALLET_TYPE.FIAT:
        return newFormatPrice(
          parseFloat(
            Math.floor(parseFloat(userPriceTotalPayouts?.price)),
          ) || 0,
          true,
          userPriceTotalPayouts?.code || '',
        );
      case WALLET_TYPE.CRYPTO:
        return `${totalPayouts || 0}${currencyInfo?.symbol || ''}`;
      case WALLET_TYPE.COINS:
        return formatNumber(totalPayouts);
      default:
        return null;
    }
  }, [type, userPriceTotalPayouts, totalPayouts, currencyInfo]);

  const handleActionClick = (e, action) => {
    e.stopPropagation();
    onSelect();
    action();
  };

  return (
    <div
      className={`wallet-credit-card${isActive ? ' wallet-credit-card--active' : ''}`}
      onClick={onSelect}
      onKeyDown={e => {
        if (e.target !== e.currentTarget) return;
        if (e.key === 'Enter') {
          onSelect();
        } else if (e.key === ' ') {
          e.preventDefault();
        }
      }}
      onKeyUp={e => {
        if (e.target !== e.currentTarget) return;
        if (e.key === ' ') onSelect();
      }}
      role="button"
      tabIndex={0}
    >
      <div className="wallet-credit-card__deco wallet-credit-card__deco--1" />
      <div className="wallet-credit-card__deco wallet-credit-card__deco--2" />

      <div className="wallet-credit-card__header">
        <div className={iconClassWrapper}>{icon}</div>
        <div className="wallet-credit-card__name">{walletName}</div>
        <div className="wallet-credit-card__chip" />
      </div>

      <div className="wallet-credit-card__balance">
        <span className="wallet-credit-card__balance-label">
          {totalBalanceTxt}
        </span>
        <span className="wallet-credit-card__balance-amount">
          {totalBalance}
        </span>
        {showAvailableBalance && (
          <span className="wallet-credit-card__balance-available">
            {t('commissions.available')}: {available}
          </span>
        )}
        {showExtraInformation && (
          <div className="wallet-credit-card__extra-info">
            <span>
              {t('rewards_coins')}: {formatNumber(coinsAvailable)}
            </span>
            {isCharityCoinsEnabled && (
              <span>
                {t('rewards_charityCoins')}:{' '}
                {formatNumber(charityCoinsAvailable)}
              </span>
            )}
          </div>
        )}
      </div>

      <div className="wallet-credit-card__footer">
        <div className="wallet-credit-card__stats">
          {totalOfPaymentsTxt && (
            <div className="wallet-credit-card__stat">
              <span className="wallet-credit-card__stat-label">
                {totalOfPaymentsTxt}
              </span>
              <span className="wallet-credit-card__stat-value">
                {totalOfPayments}
              </span>
            </div>
          )}
          {showWithdraws && (
            <div className="wallet-credit-card__stat">
              <span className="wallet-credit-card__stat-label">
                {t('commissions.total.withdraws')}
              </span>
              <span className="wallet-credit-card__stat-value">
                {totalOfPayouts}
              </span>
            </div>
          )}
        </div>

        {(showWithdrawButton || showExchangeButton) && (
          <div className="wallet-credit-card__actions">
            {showWithdrawButton && (
              <button
                type="button"
                className="wallet-credit-card__btn wallet-credit-card__btn--withdraw"
                disabled={!isWithdrawEnabled}
                onClick={e => handleActionClick(e, onWithdrawal)}
              >
                {t('commissions.withdraw')}
                <WithdrawIcon />
              </button>
            )}
            {showExchangeButton && (
              <button
                type="button"
                className="wallet-credit-card__btn wallet-credit-card__btn--exchange"
                onClick={e => handleActionClick(e, onExchange)}
              >
                {t('commissions.exchange')}
                <ExchangeIcon />
              </button>
            )}
          </div>
        )}
      </div>

      {showTransactions && (
        <button
          type="button"
          className="wallet-credit-card__btn wallet-credit-card__btn--transactions"
          onClick={e => handleActionClick(e, onSeeTransactions)}
        >
          {t('commissions.seeTransactions')}
        </button>
      )}
    </div>
  );
}

CommissionsWalletCardListItem.propTypes = {
  t: PropTypes.func.isRequired,
  wallet: PropTypes.shape(WalletProp.propType).isRequired,
  isActive: PropTypes.bool.isRequired,
  onSelect: PropTypes.func.isRequired,
  onSeeTransactions: PropTypes.func.isRequired,
  onWithdrawal: PropTypes.func.isRequired,
  onExchange: PropTypes.func.isRequired,
};

export default translate(CommissionsWalletCardListItem);
